import React from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  UserCheck,
  Shield,
  Crown,
  CheckCircle,
  Clock
} from 'lucide-react';

/**
 * UserStatsCards Component
 *
 * Displays animated statistics cards for user management dashboard
 * showing counts for different user types and statuses
 *
 * @param {Object} props - Component props
 * @param {Object} props.stats - Statistics object containing user counts
 * @param {number} props.stats.totalUsers - Total number of users
 * @param {number} props.stats.totalCustomers - Total number of customers
 * @param {number} props.stats.totalVendors - Total number of vendors
 * @param {number} props.stats.totalAdmins - Total number of admins
 * @param {number} props.stats.activeUsers - Number of active users
 * @param {number} props.stats.pendingUsers - Number of pending users
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onFilterByPending - Callback when pending card is clicked
 */
const UserStatsCards = ({ stats = {}, loading = false, onFilterByPending }) => {
  const statsCards = [
    {
      label: 'Total Users',
      value: stats.totalUsers || 0,
      icon: Users,
      color: 'blue',
      delay: 0
    },
    {
      label: 'Customers',
      value: stats.totalCustomers || 0,
      icon: UserCheck,
      color: 'green',
      delay: 0.1
    },
    {
      label: 'Vendors',
      value: stats.totalVendors || 0,
      icon: Shield,
      color: 'purple',
      delay: 0.2
    },
    {
      label: 'Admins',
      value: stats.totalAdmins || 0,
      icon: Crown,
      color: 'orange',
      delay: 0.3
    },
    {
      label: 'Active',
      value: stats.activeUsers || 0,
      icon: CheckCircle,
      color: 'green',
      delay: 0.4
    },
    {
      label: 'Pending',
      value: stats.pendingUsers || 0,
      icon: Clock,
      color: 'yellow',
      delay: 0.5,
      clickable: true,
      onClick: onFilterByPending
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        text: 'text-blue-600',
        bg: 'bg-blue-100',
        iconColor: 'text-blue-600'
      },
      green: {
        text: 'text-green-600',
        bg: 'bg-green-100',
        iconColor: 'text-green-600'
      },
      purple: {
        text: 'text-purple-600',
        bg: 'bg-purple-100',
        iconColor: 'text-purple-600'
      },
      orange: {
        text: 'text-orange-600',
        bg: 'bg-orange-100',
        iconColor: 'text-orange-600'
      },
      yellow: {
        text: 'text-yellow-600',
        bg: 'bg-yellow-100',
        iconColor: 'text-yellow-600'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
        {[...Array(6)].map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 animate-pulse"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                <div className="h-8 bg-gray-300 rounded w-16"></div>
              </div>
              <div className="p-3 bg-gray-200 rounded-lg">
                <div className="w-6 h-6"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
      {statsCards.map((card, index) => {
        const Icon = card.icon;
        const colors = getColorClasses(card.color);

        return (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: card.delay }}
            className={`
              bg-white rounded-xl p-6 shadow-sm border border-gray-200
              ${card.clickable && card.onClick ? 'cursor-pointer hover:shadow-md hover:border-gray-300 transition-all duration-200' : ''}
            `}
            onClick={card.clickable && card.onClick ? card.onClick : undefined}
            whileHover={card.clickable && card.onClick ? { scale: 1.02 } : {}}
            whileTap={card.clickable && card.onClick ? { scale: 0.98 } : {}}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{card.label}</p>
                <p className={`text-2xl font-bold ${colors.text}`}>
                  {card.value}
                </p>
              </div>
              <div className={`p-3 ${colors.bg} rounded-lg`}>
                <Icon className={`w-6 h-6 ${colors.iconColor}`} />
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default UserStatsCards;
